import React from 'react';
import { useSearchParams } from 'react-router-dom';
import AnnualCrossTopicDetailExportDocument from '../components/export/AnnualCrossTopicDetailExportDocument';
import AnnualCrossTopicProgramPlanExportDocument from '../components/export/AnnualCrossTopicProgramPlanExportDocument';
import AnnualOperationPlanExportDocument from '../components/export/AnnualOperationPlanExportDocument';
import NeisLearningContentUploadExportDocument from '../components/export/NeisLearningContentUploadExportDocument';
import { ANNUAL_CROSS_TOPIC_DETAIL_CAPTURE_CSS } from '../utils/annualCrossTopicDetailCaptureCss';
import { ANNUAL_CROSS_TOPIC_PROGRAM_PLAN_CAPTURE_CSS } from '../utils/annualCrossTopicProgramPlanCaptureCss';
import { ANNUAL_OPERATION_PLAN_CAPTURE_CSS } from '../utils/annualOperationPlanCaptureCss';
import { NEIS_LEARNING_CONTENT_UPLOAD_CAPTURE_CSS } from '../utils/neisLearningContentUploadCaptureCss';
import {
  printDocumentExportPreviewFromRoot,
  saveDocumentExportPreviewAsPdfAndClose,
} from '../utils/documentExportPreviewExport';
import {
  readDocumentExportPreviewPayload,
  type DocumentExportPreviewAction,
} from '../utils/documentExportPreviewStorage';

function parseAction(raw: string | null): DocumentExportPreviewAction {
  if (raw === 'pdf' || raw === 'print') return raw;
  return 'view';
}

function captureCssForKind(
  kind:
    | 'annual-operation-plan'
    | 'annual-cross-topic-detail'
    | 'annual-cross-topic-program-plan'
    | 'program-learning-content-detail'
    | 'neis-learning-content-upload'
): string {
  if (kind === 'annual-operation-plan') return ANNUAL_OPERATION_PLAN_CAPTURE_CSS;
  if (kind === 'neis-learning-content-upload') return NEIS_LEARNING_CONTENT_UPLOAD_CAPTURE_CSS;
  if (kind === 'annual-cross-topic-program-plan') return ANNUAL_CROSS_TOPIC_PROGRAM_PLAN_CAPTURE_CSS;
  return ANNUAL_CROSS_TOPIC_DETAIL_CAPTURE_CSS;
}

function previewTitleForPayload(
  stash: NonNullable<ReturnType<typeof readDocumentExportPreviewPayload>>
): string {
  if (stash.kind === 'annual-operation-plan') {
    return stash.printTitle;
  }
  return stash.printTitle;
}

const DocumentExportPreviewPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const stashId = searchParams.get('k') ?? '';
  const action = parseAction(searchParams.get('action'));
  const stash = React.useMemo(() => readDocumentExportPreviewPayload(stashId), [stashId]);
  const documentRef = React.useRef<HTMLDivElement | null>(null);
  const [autoActionDone, setAutoActionDone] = React.useState(false);
  const [renderReady, setRenderReady] = React.useState(false);
  const [autoError, setAutoError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      setRenderReady(true);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [stash]);

  const isAutoPdf = action === 'pdf';
  const fileName = stash ? `${stash.fileNameBase}.pdf` : 'export.pdf';
  const printTitle = stash ? previewTitleForPayload(stash) : '문서';

  const handlePrint = React.useCallback(() => {
    const root = documentRef.current;
    if (!root || !stash) return;
    void printDocumentExportPreviewFromRoot({
      root,
      title: printTitle,
      captureCss: captureCssForKind(stash.kind),
    });
  }, [printTitle, stash]);

  React.useEffect(() => {
    if (!stash || !renderReady || autoActionDone) return;
    const root = documentRef.current;
    if (!root) return;

    const timer = window.setTimeout(() => {
      if (action === 'print') {
        void printDocumentExportPreviewFromRoot({
          root,
          title: printTitle,
          captureCss: captureCssForKind(stash.kind),
        }).finally(() => setAutoActionDone(true));
        return;
      }
      if (action === 'pdf') {
        void saveDocumentExportPreviewAsPdfAndClose({
          root,
          fileName,
          captureCss: captureCssForKind(stash.kind),
        }).catch((error) => {
          console.error('PDF 저장 실패:', error);
          setAutoError('PDF 저장에 실패했습니다.');
        }).finally(() => {
          setAutoActionDone(true);
        });
      }
    }, 400);

    return () => window.clearTimeout(timer);
  }, [action, autoActionDone, fileName, printTitle, renderReady, stash]);

  if (!stash) {
    return (
      <div className="cp-document-export-preview-page">
        <p className="cp-document-export-preview-error" role="alert">
          문서 미리보기 데이터를 찾을 수 없습니다. 원래 화면에서 다시 시도해 주세요.
        </p>
      </div>
    );
  }

  const previewLabel =
    stash.kind === 'annual-operation-plan'
      ? '운영계획 미리보기'
      : stash.kind === 'program-learning-content-detail'
        ? '프로그램 내용 미리보기'
        : stash.kind === 'neis-learning-content-upload'
          ? '연간 지도 계획 미리보기'
          : stash.kind === 'annual-cross-topic-program-plan'
            ? '범교과 운영계획 미리보기'
            : '범교과 주제 미리보기';

  return (
    <div
      className={`cp-document-export-preview-page${isAutoPdf ? ' cp-document-export-preview-page--auto-pdf' : ''}`}
    >
      {!isAutoPdf ? (
        <div className="cp-document-export-preview-toolbar" aria-label="문서 인쇄">
          <p className="cp-document-export-preview-toolbar-title">{previewLabel}</p>
          <div className="cp-document-export-preview-toolbar-actions">
            <button type="button" className="cp-btn-annual-action" onClick={handlePrint}>
              프린트
            </button>
          </div>
        </div>
      ) : (
        <p className="cp-document-export-preview-auto-status" role="status" aria-live="polite">
          {autoError ?? 'PDF를 저장하는 중입니다…'}
        </p>
      )}

      <div className="cp-document-export-preview-body">
        {stash.kind === 'annual-operation-plan' ? (
          <AnnualOperationPlanExportDocument ref={documentRef} payload={stash.data} />
        ) : stash.kind === 'neis-learning-content-upload' ? (
          <NeisLearningContentUploadExportDocument ref={documentRef} payload={stash.data} />
        ) : stash.kind === 'annual-cross-topic-program-plan' ? (
          <AnnualCrossTopicProgramPlanExportDocument
            ref={documentRef}
            payload={stash.data}
            seqColumnLabel="순서"
          />
        ) : (
          <AnnualCrossTopicDetailExportDocument
            ref={documentRef}
            payload={stash.data}
            seqColumnLabel="순서"
          />
        )}
      </div>
    </div>
  );
};

export default DocumentExportPreviewPage;
